import { Euler, Group, Matrix4, Mesh, Quaternion, type Texture, Vector3 } from 'three';
import type { SceneryPlan } from '../../track/scenery';
import { GeoBuilder, hash01, type Proto, trs } from '../geo';
import { look, type TreeKind } from '../look';
import { PAL } from '../palette';
import type { Terrain } from '../terrain';
import { type Atlas, brakingBoardAtlas, sponsorAtlas } from '../textures';
import { toonMaterial, vertexColorToon } from '../toon';
import { Crowd, type Spectator } from './crowd';
import {
  baleProto,
  baleStackProto,
  boardFrameProto,
  boatProto,
  breakwaterProto,
  bushProto,
  cactusProto,
  citrusProto,
  cypressProto,
  larchProto,
  lighthouseProto,
  logStackProto,
  logsProto,
  oleanderProto,
  palmProto,
  seafrontProto,
  spruceProto,
  faceQuad,
  farmhouseProto,
  fenceProto,
  flagProto,
  gantryProto,
  grandstandProto,
  oliveProto,
  pineProto,
  townProto,
  wallProto,
} from './kit';

export interface SceneryRender {
  group: Group;
  crowd: Crowd;
  /** Grandstand position (three.js x, z) for crowd noise. */
  grandstandAt: [number, number];
}

const CHUNK = 260;

const TREES: Record<TreeKind, (v: number) => Proto> = {
  cypress: cypressProto,
  pine: pineProto,
  olive: oliveProto,
  bush: bushProto,
  spruce: spruceProto,
  larch: larchProto,
  palm: palmProto,
  cactus: cactusProto,
  oleander: oleanderProto,
  citrus: citrusProto,
};

/**
 * Stamps the planned scenery into a handful of merged, chunked meshes (so the
 * shadow pass can cull by chunk), plus textured sign faces and the animated crowd.
 */
export function buildScenery(plan: SceneryPlan, terrain: Terrain, seed: number, lowQuality = false): SceneryRender {
  const group = new Group();
  const chunks = new Map<string, GeoBuilder>();
  const chunk = (x: number, z: number): GeoBuilder => {
    const k = `${Math.floor(x / CHUNK)},${Math.floor(z / CHUNK)}`;
    let b = chunks.get(k);
    if (!b) {
      b = new GeoBuilder();
      chunks.set(k, b);
    }
    return b;
  };
  const cache = new Map<string, Proto>();
  const proto = (key: string, make: () => Proto): Proto => {
    let p = cache.get(key);
    if (!p) {
      p = make();
      cache.set(key, p);
    }
    return p;
  };

  // The setting (look.ts): what each planned tree becomes, and the sea nothing may stand in.
  const L = look();
  // One of three of each tree. A negative `v` (a palm's place along the coast) wraps round too: read as -1 or -2,
  // the variant found nothing to build with, and the palm came out of NaNs (unseen, and the console's warnings).
  const tree = (kind: TreeKind, v: number): Proto => {
    const k = ((v % 3) + 3) % 3;
    return proto(kind + k, () => TREES[kind](k));
  };
  const pick = (k: keyof typeof L.flora, v: number): TreeKind => L.flora[k][v % L.flora[k].length];
  // The water at the day's own level (low on a hilly day: see SeaPlan.level).
  const sea = terrain.seaPlan && L.sea ? { ...L.sea, level: terrain.seaPlan.level } : null;
  const dry = (sx: number, sy: number): boolean => !sea || terrain.heightAt(sx, sy) > sea.level + 0.6;

  const sponsors = sponsorAtlas();
  const boards = brakingBoardAtlas();
  const sponsorFaces = new GeoBuilder(true);
  const boardFaces = new GeoBuilder(true);
  const people: Spectator[] = [];

  const stamp = (p: Proto, sx: number, sy: number, yaw: number, scale = 1, lift = 0): void => {
    const x = sx;
    const z = -sy;
    const h = terrain.heightAt(sx, sy) + lift;
    chunk(x, z).addProto(p, trs(x, h, z, yaw, scale));
  };

  /** A straight run (wall or fence section, `len` long along its local x) tilted to follow the ground. */
  const stampAlong = (p: Proto, sx: number, sy: number, yaw: number, len: number): void => {
    const cx = (Math.cos(yaw) * len) / 2;
    const cy = (Math.sin(yaw) * len) / 2;
    const h0 = terrain.heightAt(sx - cx, sy - cy);
    const h1 = terrain.heightAt(sx + cx, sy + cy);
    const q = new Quaternion().setFromEuler(new Euler(0, yaw, Math.atan2(h1 - h0, len), 'YZX'));
    chunk(sx, -sy).addProto(p, new Matrix4().compose(new Vector3(sx, (h0 + h1) / 2, -sy), q, new Vector3(1, 1, 1)));
  };

  for (const it of plan.items) {
    if (!dry(it.x, it.y)) continue;
    switch (it.kind) {
      case 'cypress':
      case 'pine':
      case 'olive':
      case 'bush':
        stamp(tree(pick(it.kind, it.variant), it.variant), it.x, it.y, it.rot, it.scale);
        break;
      case 'bale':
        if (L.bales === 'bale') stamp(proto('bale', baleProto), it.x, it.y, it.rot, 1);
        else if (L.bales === 'logs') stamp(proto('logs', logsProto), it.x, it.y, it.rot, 1);
        break;
      case 'baleStack':
        if (L.bales === 'bale') stamp(proto('baleStack', baleStackProto), it.x, it.y, it.rot, 1);
        else if (L.bales === 'logs') stamp(proto('logStack', logStackProto), it.x, it.y, it.rot, 1);
        break;
      case 'wall':
        // The setting's low walls: dry stone in its colours, or (the Dolomites) wooden fences.
        if (L.walls.kind === 'fence') stampAlong(proto('fence', fenceProto), it.x, it.y, it.rot, 4.3);
        else stampAlong(proto('wall' + (it.variant % 8), () => wallProto(it.variant % 8)), it.x, it.y, it.rot, 4.3);
        break;
      case 'fence':
        stampAlong(proto('fence', fenceProto), it.x, it.y, it.rot, 4.1);
        break;
      case 'farmhouse':
        stamp(proto('farmhouse' + (it.variant % 2), () => farmhouseProto(it.variant % 2)), it.x, it.y, it.rot, it.scale);
        break;
      case 'flag':
        stamp(proto('flag' + (it.variant % 2), () => flagProto(it.variant % 2)), it.x, it.y, it.rot, 1);
        break;
      case 'sponsor': {
        const W = 3.8;
        const H = 0.95;
        const lift = 0.45;
        stamp(proto('sponsorFrame', () => boardFrameProto(W, H, lift, lift + H + 0.05)), it.x, it.y, it.rot);
        sponsorFaces.add(faceQuad(W, H, lift, sponsors.uv(it.variant % sponsors.count)), PAL.cream, trs(it.x, terrain.heightAt(it.x, it.y), -it.y, it.rot), true);
        break;
      }
      case 'brakingBoard': {
        const W = 1.55;
        const H = 0.95;
        const lift = 0.32;
        stamp(proto('bbFrame', () => boardFrameProto(W, H, lift, lift + H)), it.x, it.y, it.rot);
        boardFaces.add(faceQuad(W, H, lift, boards.uv(it.variant % boards.count)), PAL.cream, trs(it.x, terrain.heightAt(it.x, it.y), -it.y, it.rot), true);
        break;
      }
      case 'spectator':
        people.push({ x: it.x, y: terrain.heightAt(it.x, it.y), z: -it.y, yaw: it.rot, scale: it.scale, variant: it.variant });
        break;
    }
  }

  // Grandstand with its seated crowd.
  const gs = plan.grandstand;
  stamp(proto('grandstand', () => grandstandProto(gs.length, gs.depth)), gs.x, gs.y, gs.rot);
  {
    const cos = Math.cos(gs.rot);
    const sin = Math.sin(gs.rot);
    const base = terrain.heightAt(gs.x, gs.y);
    let k = 0;
    for (let tier = 0; tier < 4; tier++) {
      const lx = gs.depth / 2 - 1.4 - tier * 1.7;
      const ly = 1.3 + tier * 0.62 + 0.15;
      for (let lz = -gs.length / 2 + 1; lz < gs.length / 2 - 0.6; lz += 0.78) {
        if (hash01(k++, 31) < 0.12) continue;
        // Local (lx, lz) -> world, rotated by rot about +y.
        const wx = gs.x + lx * cos + lz * sin;
        const wz = -gs.y - lx * sin + lz * cos;
        people.push({
          x: wx,
          y: base + ly - 0.55,
          z: wz,
          yaw: gs.rot,
          scale: 0.92 + hash01(k, 5) * 0.12,
          variant: Math.floor(hash01(k, 9) * 12),
          seated: true,
        });
      }
    }
  }

  // Start/finish gantry.
  const gt = plan.gantry;
  stamp(proto('gantry', () => gantryProto(gt.span)), gt.x, gt.y, gt.rot);

  // The hilltop town and cypresses on its slopes.
  const town = plan.town;
  const townH = terrain.heightAt(town.x, town.y);
  chunk(town.x, -town.y).addProto(proto('town', () => townProto(seed)), trs(town.x, townH - 2, -town.y, town.rot, 2.0));
  for (let k = 0; k < 46; k++) {
    const a = hash01(seed, k) * Math.PI * 2;
    const r = 110 + hash01(seed + 1, k) * 220;
    const x = town.x + Math.cos(a) * r;
    const y = town.y + Math.sin(a) * r;
    if (dry(x, y)) stamp(tree(L.townTrees[k % L.townTrees.length], k), x, y, a, 0.9 + hash01(k, 3) * 0.4);
  }

  // Distant trees dotting the hills.
  const b = plan.bounds;
  const span = Math.max(b.maxX - b.minX, b.maxY - b.minY) / 2 + 1100;
  for (let k = 0; k < (lowQuality ? 200 : 520); k++) {
    const x = b.cx + (hash01(seed + 11, k) * 2 - 1) * span;
    const y = b.cy + (hash01(seed + 12, k) * 2 - 1) * span;
    if (terrain.prox.clearance(x, y, 200) < 120) continue;
    if (!dry(x, y)) continue;
    // The setting's mix of far trees (Tuscany's: cypresses 62%, olives 23%, pines 15%).
    const r = hash01(seed + 13, k);
    let acc = 0;
    let kind: TreeKind = L.farTrees[L.farTrees.length - 1][0];
    for (const [t, share] of L.farTrees) {
      acc += share;
      if (r < acc) {
        kind = t;
        break;
      }
    }
    const scale = kind === 'olive' || kind === 'citrus' ? 1.2 : kind === 'pine' ? 1 : 0.9 + hash01(k, 4) * 0.5;
    stamp(tree(kind, k), x, y, k, scale);
  }

  // A harbour on the day's coast: a breakwater with its light, boats moored inside it and a few out at sea.
  if (sea) {
    const s = terrain.seaPlan!;
    const at = (along: number, lat: number): [number, number] => [s.cx + s.dx * along - s.dy * lat, s.cy + s.dy * along + s.dx * lat];
    const onWater = (p: Proto, sx: number, sy: number, yaw: number, lift = 0): void => {
      chunk(sx, -sy).addProto(p, trs(sx, sea.level + lift, -sy, yaw));
    };
    // Where the water actually starts at `lat` (the shore slopes down past the coast line).
    const waterAlong = (lat: number): number => {
      let along = terrain.coastAlong(lat);
      for (let k = 0; k < 80; k++, along += 6) {
        const [x, y] = at(along, lat);
        if (terrain.heightAt(x, y) < sea.level - 0.5) break;
      }
      return along;
    };
    const lat0 = 120;
    const c0 = waterAlong(lat0);
    // The breakwater runs out from the shore at an angle, its light at the end.
    const dirYaw = Math.atan2(s.dy, s.dx);
    const arm = dirYaw - 0.55;
    for (let k = 0; k < 26; k++) {
      const [bx, by] = at(c0 - 14, lat0);
      const x = bx + Math.cos(arm) * k * 8;
      const y = by + Math.sin(arm) * k * 8;
      onWater(proto('breakwater' + (k % 3), () => breakwaterProto(k % 3)), x, y, arm, 0);
      if (k === 25) onWater(proto('lighthouse', lighthouseProto), x + Math.cos(arm) * 6, y + Math.sin(arm) * 6, 0, 1.8);
    }
    // The waterfront: tall coloured houses facing the water beside the breakwater's root, palms along the shore.
    for (let k = 0; k < 3; k++) {
      const lat = lat0 - 62 - k * 60;
      const along = terrain.coastAlong(lat) - 34;
      const [x, y] = at(along, lat);
      if (terrain.heightAt(x, y) > sea.level + 0.6) stamp(proto('seafront' + k, () => seafrontProto(seed + k * 11)), x, y, dirYaw + Math.PI / 2, 1, -0.5);
    }
    for (let lat = lat0 - 520; lat < lat0 + 520; lat += 26) {
      const along = terrain.coastAlong(lat) - 16;
      const [x, y] = at(along, lat);
      if (terrain.heightAt(x, y) > sea.level + 0.6 && terrain.prox.clearance(x, y, 40) > 18) stamp(tree('palm', lat), x, y, lat, 1);
    }
    // Moored in its lee, then a few out on the water.
    for (let k = 0; k < 14; k++) {
      const lat = lat0 - 34 - (k % 7) * 16 - hash01(seed + 21, k) * 6;
      const along = waterAlong(lat) + 10 + Math.floor(k / 7) * 20 + hash01(seed + 22, k) * 6;
      const [x, y] = at(along, lat);
      onWater(proto('boat' + (k % 3 === 2 ? 0 : k % 3), () => boatProto(k % 3 === 2 ? 0 : k % 3)), x, y, dirYaw + Math.PI / 2 + (hash01(k, 7) - 0.5) * 0.3);
    }
    for (let k = 0; k < 9; k++) {
      const lat = (hash01(seed + 23, k) * 2 - 1) * 1300;
      const along = waterAlong(lat) + 180 + hash01(seed + 24, k) * 900;
      const [x, y] = at(along, lat);
      onWater(proto('boat' + (k % 3), () => boatProto(k % 3)), x, y, hash01(k, 8) * Math.PI * 2);
    }
  }

  for (const [key, builder] of chunks) {
    const m = new Mesh(builder.build(), vertexColorToon());
    m.name = 'chunk ' + key;
    m.castShadow = true;
    m.receiveShadow = true;
    group.add(m);
  }
  const faceMesh = (bld: GeoBuilder, atlas: Atlas): void => {
    if (bld.vertexCount === 0) return;
    const m = new Mesh(bld.build(), toonMaterial(0xffffff, { map: atlas.texture as Texture }));
    m.receiveShadow = true;
    group.add(m);
  };
  faceMesh(sponsorFaces, sponsors);
  faceMesh(boardFaces, boards);

  const crowd = new Crowd(people);
  group.add(crowd.group);
  return { group, crowd, grandstandAt: [gs.x, -gs.y] };
}
